import React from "react";
import { connect } from "react-redux";
import { Switch, Router, Route } from "react-router-dom";
import { LoginCallback, Security } from "@okta/okta-react";
import { toRelativeUrl } from "@okta/okta-auth-js";

import { currentUserIsCitybaseAdmin } from "@/components/currentUser/CurrentUser.state";
import { userIsLoggedIn } from "@/components/loginPage/LoginPage.selectors";
import { OKTA_WIDGET_CALLBACK_QS } from "@/constants/routes";
import { getCustomerManagementEnabled } from "@/state/client.state";
import { history } from "@/util/history";
import { userIsAuthenticated } from "@/util/session";
import {
  AppRedirect,
  AppRoute,
  MaintenanceRouter,
  RoutesBuilder,
  filterRedirects,
  filterRoutes,
  getRedirectsConfig,
  getRoutesConfig,
} from "./routing";

const restoreOriginalUri = (history) => (_oktaAuth, originalUri) => {
  history.replace(
    toRelativeUrl(
      originalUri || `/login?${OKTA_WIDGET_CALLBACK_QS}`,
      window.location.origin
    )
  );
};

/**
 * Defines the routes and redirects in Revenue Management.
 */
export const Routes = ({
  oktaAuth,
  isLoggedIn,
  isCitybaseAdmin,
  isCustomerManagementEnabled,
  useMaintenanceScreen,
}) => {
  // Optionally render an "Under Maintenance" screen
  // depending on the Launch Darkly maintenance feature flag
  if (useMaintenanceScreen) return MaintenanceRouter({ history });

  // Builds router definitions. To add new app routes or other options effecting
  // the paths, modify `routing/paths.js`.
  const routes = new RoutesBuilder(AppRoute, getRoutesConfig, filterRoutes)
    .configure({ isCustomerManagementEnabled, isLoggedIn })
    .filter({
      enabled: true,
      adminRoute: isCitybaseAdmin,
      protectedRoute: isLoggedIn,
    })
    .build();

  const redirects = new RoutesBuilder(
    AppRedirect,
    getRedirectsConfig,
    filterRedirects
  )
    .configure()
    .filter({
      enabled: true,
      adminRoute: isCitybaseAdmin,
      protectedRoute: isLoggedIn,
    })
    .build();

  const appRouting = routes.concat(redirects);

  return (
    <Router history={history}>
      <Security
        oktaAuth={oktaAuth}
        onAuthRequired={({ history }) => history.push("/login")}
        restoreOriginalUri={restoreOriginalUri(history)}
      >
        <Switch>
          <Route path="/login/callback" component={LoginCallback} />
          {appRouting}
        </Switch>
      </Security>
    </Router>
  );
};

const mapStateToProps = (state) => ({
  isLoggedIn: userIsLoggedIn(state),
  isCitybaseAdmin: currentUserIsCitybaseAdmin(state),
  isAuthenticated: userIsAuthenticated(state),
  isCustomerManagementEnabled: getCustomerManagementEnabled(state),
  oidc: state.config.oidc,
});

export default connect(mapStateToProps, Routes)(Routes);
